import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Check } from 'lucide-react'
import styled from 'styled-components'
import Modal from 'components/_UI/Modal'
import Text from 'components/_UI/Text'
import Button from 'components/_UI/Button'
import paths from 'routes/paths'
import colors from 'theme/colors'

/**
 * Antes de criar o primeiro evento a conta precisa de perfil de produtor e de
 * dados bancários. Em vez de um aviso por vez (um por cima do outro), mostra
 * as duas pendências juntas, com o que já foi feito marcado. O perfil leva à
 * página dele e volta para a criação do evento ao salvar; os dados bancários
 * abrem no lugar deste modal.
 */
export default function EventSetupModal({ isOpen, hasProfile, hasBank, onClose, onOpenBank }) {
    const navigate = useNavigate()
    const pending = [hasProfile, hasBank].filter(done => !done).length
    const profileIsNext = !hasProfile

    function goToProfile() {
        navigate(paths.dashboard.profile, { state: { backToCreateEvent: true } })
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} closeButton>
            <Content>
                <Text name='highlight' color={colors.nightDark} marginBottom='6px'>
                    {pending === 1 ? 'Falta só mais um passo' : 'Falta pouco para criar seu evento'}
                </Text>
                <Text color={colors.night} marginBottom='18px'>
                    {pending === 1
                        ? 'Complete o que falta e o formulário do evento abre em seguida.'
                        : 'Antes de começar, precisamos de duas informações da sua conta.'}
                </Text>

                <Steps>
                    <Step
                        number={1}
                        done={hasProfile}
                        title='Perfil de produtor'
                        description='O nome e a foto que o público vê nos seus eventos.'
                        action='Criar perfil'
                        primary={profileIsNext}
                        onClick={goToProfile}
                    />
                    <Step
                        number={2}
                        done={hasBank}
                        title='Dados bancários'
                        description='A conta onde você recebe o valor das vendas.'
                        action='Cadastrar conta'
                        primary={!profileIsNext}
                        onClick={onOpenBank}
                    />
                </Steps>
            </Content>
        </Modal>
    )
}

function Step({ number, done, title, description, action, primary, onClick }) {
    return (
        <StepRow $done={done}>
            <Mark $done={done}>{done ? <Check size={16} strokeWidth={3} /> : number}</Mark>

            <StepText>
                <Text color={colors.nightDark} type='bold' paddingBottom='2px'>{title}</Text>
                <Text color={colors.night} paddingBottom='0'>
                    {done ? 'Tudo certo por aqui.' : description}
                </Text>
            </StepText>

            {!done && (
                <Button size='small' variant={primary ? 'featured' : 'light'} onClick={onClick}>
                    {action}
                </Button>
            )}
        </StepRow>
    )
}

const Content = styled.div`
    width: 100%;
    text-align: left;
    padding-top: 6px;
`

const Steps = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const StepRow = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px;
    border-radius: 14px;
    border: 1px solid ${({ $done }) => $done ? colors.success : '#E6E6EC'};
    background: ${({ $done }) => $done ? 'rgba(43, 200, 102, 0.07)' : colors.neutralLight};

    @media screen and (max-width: 520px) {
        flex-wrap: wrap;
        gap: 10px;
        padding: 12px;

        button {
            width: 100%;
        }
    }
`

const Mark = styled.div`
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    color: ${({ $done }) => $done ? colors.neutralLight : colors.occasionalPurple};
    background: ${({ $done }) => $done ? colors.success : 'rgba(105, 12, 228, 0.1)'};
`

const StepText = styled.div`
    flex: 1;
    min-width: 160px;
`
